import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import Section from '../Section'
import ArticleCardNew from '../ArticleCardNew'

type Image = {
    alt: string
    caption: string
    link: string
    type
}

interface ArticleObj {
    id: string,
    url: {
        href: string
    },
    title: string
    image: {
        link: string,
        alt: string,
        title: string,
        caption: string,
    },
    type: string,
    solutions: { value: string, label: string }[]
}

interface Props {
    title: string
    intro: string
    endpoint: string
    highlightedArticle: {
        title: string
        href: string
        imageSrc: Image
        imageSrcSmall: Image
        type: string
        solutions: string[]
    }
    firstPageArticles: ArticleObj[]
    hasSecondPage: boolean
    solutions: any[]
    strings: {
        loadMore: string // Load more
    }
}

export default function ArticlesOverview({ title, intro, endpoint, highlightedArticle, firstPageArticles, hasSecondPage, solutions, strings }: Props) {
    const [activeContentFilter, setActiveContentFilter] = useState<string>('')
    const [activeSolutionFilter, setActiveSolutionFilter] = useState<string>('')
    const [articles, setArticles] = useState<ArticleObj[]>(firstPageArticles)
    const [hasNextPage, setHasNextPage] = useState<boolean>(!!hasSecondPage)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)
    const [isLoadingFilter, setIsLoadingFilter] = useState<boolean>(false)
    const [error, setError] = useState<any>()

    async function handleFilterClick(paramValue: string, paramName: 'type' | 'solution') {
        const isTypeAndIsTheSameFilter = paramName === 'type' && paramValue === activeContentFilter
        const isSolutionAndIsTheSameFilter = paramName === 'solution' && paramValue === activeSolutionFilter
        if (isTypeAndIsTheSameFilter || isSolutionAndIsTheSameFilter) return

        setIsLoadingFilter(true)

        setCurrentPage(1)
        setArticles([])

        const isTypeParam = paramName === 'type'
        const isSolutionParam = paramName === 'solution'

        isTypeParam && setActiveContentFilter(paramValue)
        isSolutionParam && setActiveSolutionFilter(paramValue !== 'All' ? paramValue : '')

        const typeParam = isTypeParam ? paramValue : activeContentFilter
        const solutionParam = isSolutionParam ? paramValue : activeSolutionFilter

        const currentUrl = new URL(window.location.href)
        const params = new URLSearchParams(currentUrl.search)
        updateParams(params, isTypeParam, 'type', activeContentFilter, paramValue)
        updateParams(params, isSolutionParam, 'solution', activeSolutionFilter, paramValue)
        currentUrl.search = params.toString()
        window.history.replaceState({}, '', currentUrl.href)

        setError(undefined)
        await updateArticles(typeParam, solutionParam)

        setIsLoadingFilter(false)
    }

    async function updateArticles(type, solution) {
        const typeParam = type ? `&type=${type}` : ''
        let solutionId = getSolutionIdFromLabel(solution)
        const solutionParam = solutionId ? `&solution=${solutionId}` : ''
        try {
            const res = await fetch(endpoint + typeParam + solutionParam)
            const resJson = await res.json()
            if (resJson.hits) {
                setArticles(resJson.hits)
                setHasNextPage(resJson.hasNextPage)
            } else {
                throw Error(`Error fetching articles with these filters`)
            }
        } catch (err) {
            setError(err)
        }
    }

    function updateParams(params, isParam, key, activeFilter, paramValue) {
        const paramToString = paramValue.toString()
        const paramToUse = paramToString !== 'All' && paramToString
        if (isParam) {
            paramToUse ? params.set(key, paramToUse) : params.delete(key)
        } else {
            activeFilter ? params.set(key, activeFilter) : params.delete(key)
        }
    }

    function getSolutionIdFromLabel(label: string) {
        if (!label) return ''
        const currentSolution = solutions.find(solution => solution.label === label)
        return currentSolution?.value
    }

    async function loadMoreItems() {
        setIsLoadingMore(true)

        const typeParam = activeContentFilter ? `&type=${activeContentFilter}` : ''
        const solutionParam = activeSolutionFilter ? `&solution=${activeSolutionFilter}` : ''

        const serviceRes = await fetch(endpoint + `&page=${currentPage + 1}${typeParam}${solutionParam}`)
        const serviceResJson = await serviceRes.json()

        if (serviceResJson.hits) {
            setArticles(prevState => [...prevState, ...serviceResJson.hits])
            setHasNextPage(serviceResJson.hasNextPage)
            setCurrentPage(prevState => prevState + 1)
        }

        setIsLoadingMore(false)
    }

    const articleTypes = [
        { label: 'All', value: '' },
        { label: 'News', value: 'news' },
        { label: 'Insights', value: 'insights' }
    ]

    const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(false)

    const toggleFilters = () => {
        setIsAccordionOpen((prev) => !prev)
    }

    useEffect(() => {
        const initialUrl = new URL(window.location.href)
        const initialParams = new URLSearchParams(initialUrl.search)
        const initialTypeParam = initialParams.get('type')
        const initialSolutionParam = initialParams.get('solution')

        setActiveContentFilter(initialTypeParam || '')
        setActiveSolutionFilter(initialSolutionParam || '')
        if (initialTypeParam || initialSolutionParam) {
            updateArticles(initialTypeParam || '', initialSolutionParam || '')
        }
    }, [])

    return (
        <div className='page--news-and-insights'>

            <Section textColor='text-dark' className='top-section' bgColor='bg-beige-050'>
                <div className='grid-container'>
                    <h1 className='title-lg title'>
                        {title}
                    </h1>
                </div>
            </Section>

            <Section textColor='text-dark' className='filters-section' bgColor='bg-beige-050'>
                <div className='grid-container'>
                    <div className='filters-section__wrapper'>
                        <button
                            className={clsx(
                                'accordion__title',
                                isAccordionOpen && 'accordion__title--active'
                            )}
                            onClick={toggleFilters}
                        >

                            {isAccordionOpen ? 'Hide Filters' : 'Show Filters'}

                            <svg width='16' height='10' viewBox='0 0 16 10' xmlns='http://www.w3.org/2000/svg'><path d='M2.00033 0.727295L8.25033 6.9773L14.5003 0.727295L15.7731 2.00009L8.25033 9.52288L0.727539 2.00009L2.00033 0.727295Z' /></svg>

                        </button>

                        <div className={clsx(
                            'accordion__content',
                            isAccordionOpen && 'accordion__content--active'
                        )}>
                            <div>
                                <div className='filters'>

                                    {articleTypes && (
                                        <div className='filters-wrapper'>

                                            <p className='filters-wrapper__title'>
                                                Filter by Content Type
                                            </p>

                                            <div className='filters-wrapper__content'>
                                                {articleTypes.map(articleType => (
                                                    <button
                                                        key={articleType.value}
                                                        onClick={() => handleFilterClick(articleType.value, 'type')}
                                                        className={clsx(activeContentFilter === articleType.value ? 'active' : '')}
                                                    >
                                                        {articleType.label}
                                                    </button>
                                                ))}
                                            </div>

                                        </div>
                                    )}

                                    {solutions && (
                                        <div className='filters-wrapper'>

                                            <p className='filters-wrapper__title'>
                                                Filter by Solution
                                            </p>

                                            <div className='filters-wrapper__content'>
                                                {solutions.map(solution => (
                                                    <button
                                                        key={solution.value}
                                                        onClick={() => handleFilterClick(solution.label, 'solution')}
                                                        className={clsx((activeSolutionFilter === solution.label || (!activeSolutionFilter && solution.label === 'All')) ? 'active' : '')}
                                                    >
                                                        {solution.label}
                                                    </button>
                                                ))}
                                            </div>

                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </Section>

            {highlightedArticle && <Section textColor='text-dark' className='featured-article' bgColor='bg-beige-050'>
                <div className='grid-container'>
                    <ArticleCardNew
                        title={highlightedArticle.title}
                        featured={true}
                        href={{
                            url: highlightedArticle.href,
                            isExternal: false
                        }}
                        img={highlightedArticle.imageSrc && {
                            src: highlightedArticle.imageSrc.link,
                            srcSmall: highlightedArticle.imageSrcSmall.link,
                            alt: highlightedArticle.imageSrc.alt,
                        }}
                        category={highlightedArticle.type as "news" | "insights"}
                        customCategory={solutions}
                    />
                </div>
            </Section>}

            <Section textColor='text-dark' className='all-articles' bgColor='bg-beige-050'>

                <div id='all-articles'></div>

                {error && (
                    <div className='grid-container'>
                        <div className='all-articles__error'>

                            <svg width="36" height="30" viewBox="0 0 36 30" xmlns="http://www.w3.org/2000/svg"><path d="M5.6875 0.96875H35.875V25.4689C35.875 26.4137 35.4997 27.3198 34.8316 27.9879C34.1635 28.656 33.2573 29.0314 32.3125 29.0314H4.125V27.0314C4.5394 27.0314 4.93683 26.8667 5.22985 26.5737C5.52288 26.2807 5.6875 25.8833 5.6875 25.4689V0.96875ZM7.32656 27.0314H32.3125C32.7269 27.0314 33.1243 26.8667 33.4174 26.5737C33.7104 26.2807 33.875 25.8833 33.875 25.4689V2.96875H7.6875V25.4689C7.6875 26.0155 7.56186 26.5492 7.32656 27.0314Z M2.5625 9.09375V25.4688C2.5625 25.8832 2.72712 26.2806 3.02015 26.5736C3.31317 26.8666 3.7106 27.0314 4.125 27.0314L5.125 27.0312V29.0312L4.125 29.0314C3.18017 29.0314 2.27403 28.6559 1.60593 27.9878C0.937834 27.3197 0.5625 26.4136 0.5625 25.4688V9.09375H2.5625Z M21.5 13V22H19.5V13H21.5Z M20.5 11C21.3284 11 22 10.3284 22 9.5C22 8.67157 21.3284 8 20.5 8C19.6716 8 19 8.67157 19 9.5C19 10.3284 19.6716 11 20.5 11Z" /></svg>

                            <p className='text-lg'>
                                An error has occurred when fetching articles.
                            </p>

                        </div>
                    </div>
                )}

                {isLoadingFilter && (
                    <div className='grid-container'>
                        <div className='all-articles__loading'>
                            <svg width='512' height='512' xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" d="M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z" /><path d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z" /></svg>
                        </div>
                    </div>
                )}

                {!isLoadingFilter && !error && !articles.length && <div className='grid-container'>
                    <div className='all-articles__error'>

                        <svg width="36" height="30" viewBox="0 0 36 30" xmlns="http://www.w3.org/2000/svg"><path d="M5.6875 0.96875H35.875V25.4689C35.875 26.4137 35.4997 27.3198 34.8316 27.9879C34.1635 28.656 33.2573 29.0314 32.3125 29.0314H4.125V27.0314C4.5394 27.0314 4.93683 26.8667 5.22985 26.5737C5.52288 26.2807 5.6875 25.8833 5.6875 25.4689V0.96875ZM7.32656 27.0314H32.3125C32.7269 27.0314 33.1243 26.8667 33.4174 26.5737C33.7104 26.2807 33.875 25.8833 33.875 25.4689V2.96875H7.6875V25.4689C7.6875 26.0155 7.56186 26.5492 7.32656 27.0314Z M2.5625 9.09375V25.4688C2.5625 25.8832 2.72712 26.2806 3.02015 26.5736C3.31317 26.8666 3.7106 27.0314 4.125 27.0314L5.125 27.0312V29.0312L4.125 29.0314C3.18017 29.0314 2.27403 28.6559 1.60593 27.9878C0.937834 27.3197 0.5625 26.4136 0.5625 25.4688V9.09375H2.5625Z M21.5 13V22H19.5V13H21.5Z M20.5 11C21.3284 11 22 10.3284 22 9.5C22 8.67157 21.3284 8 20.5 8C19.6716 8 19 8.67157 19 9.5C19 10.3284 19.6716 11 20.5 11Z" /></svg>

                        <p className='text-lg'>
                            No articles found.
                        </p>

                    </div>
                </div>}

                {(!error && !isLoadingFilter && articles.length) ? <div className='grid-container'>
                    <div className='grid-row'>
                        {articles.map((item, i) => (
                            <div className='c-sm-6 c-lg-4' key={i}>
                                <ArticleCardNew
                                    featured={false}
                                    href={{
                                        url: item.url.href,
                                        isExternal: false
                                    }}
                                    img={{
                                        src: item.image?.link,
                                        alt: item.image?.alt
                                    }}
                                    category={item.type as 'news' | 'insights'}
                                    customCategory={item.solutions?.map(solution => solution.label)}
                                    title={item.title}
                                />
                            </div>
                        ))}
                    </div>
                    {hasNextPage && (
                        <div className='load-more'>
                            {isLoadingMore ? <button>
                                Loading...
                            </button> : <button onClick={loadMoreItems}>
                                {strings.loadMore} <svg width='14' height='14' fill='currentColor' viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.6998 0.800049V6.30005H13.1998V7.70005H7.6998V13.2H6.2998V7.70005H0.799805V6.30005H6.2998V0.800049H7.6998Z' /></svg>
                            </button>}
                        </div>
                    )}
                </div> : <></>}
            </Section>
        </div>
    )
}
